import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Modal,
  Badge,
} from "react-bootstrap";
import { apiGet } from "../../../Services";
import { Helmet } from "react-helmet-async";
import {
  AiOutlineLeft,
  AiOutlineRight,
  AiOutlineZoomIn,
  AiOutlineZoomOut,
  AiOutlineClose,
  AiOutlineArrowLeft,
} from "react-icons/ai";
import "./Gallery.css"; // We'll create this CSS file

const PublicGallery = () => {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [images, setImages] = useState([]);
  const [lightbox, setLightbox] = useState({ show: false, index: 0, zoom: 1 });
  const [loading, setLoading] = useState(true);

  // Fetch all groups with preview
  const fetchGroups = async () => {
    try {
      setLoading(true);
      const res = await apiGet("/api/gallery/groups");
      setGroups(res.data);
    } catch (err) {
      console.error("Failed to fetch groups");
    } finally {
      setLoading(false);
    }
  };

  // Fetch images of a selected group
  const fetchGroupImages = async (groupName) => {
    try {
      setLoading(true);
      const res = await apiGet(`/api/gallery/group/${groupName}`);
      setImages(res.data);
      setSelectedGroup(groupName);
    } catch (err) {
      console.error("Failed to fetch group images");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  // Lightbox controls
  const openLightbox = (index) => setLightbox({ show: true, index, zoom: 1 });
  const closeLightbox = () => setLightbox({ show: false, index: 0, zoom: 1 });
  const nextImage = () =>
    setLightbox((prev) => ({
      ...prev,
      index: (prev.index + 1) % images.length,
      zoom: 1,
    }));
  const prevImage = () =>
    setLightbox((prev) => ({
      ...prev,
      index: (prev.index - 1 + images.length) % images.length,
      zoom: 1,
    }));
  const zoomIn = () =>
    setLightbox((prev) => ({ ...prev, zoom: Math.min(prev.zoom + 0.2, 3) }));
  const zoomOut = () =>
    setLightbox((prev) => ({ ...prev, zoom: Math.max(prev.zoom - 0.2, 1) }));

  // Handle swipe for mobile
  const handleTouchStart = (e) => {
    setLightbox((prev) => ({ ...prev, touchStartX: e.touches[0].clientX }));
  };

  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchEndX - lightbox.touchStartX;

    if (Math.abs(diff) > 50) {
      // Minimum swipe distance
      if (diff > 0) {
        prevImage();
      } else {
        nextImage();
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Gallery</title>
        <meta
          name="description"
          content="Explore our image gallery by groups."
        />
        <meta
          name="keywords"
          content="gallery, images, collections, Manav Adhikar Forum"
        />
        <link rel="canonical" href="https://manavadhikarforum.in/Gallery" />
      </Helmet>

      <Container className="gallery-container ">
        {!selectedGroup && (
          <>
            {/* <div className="gallery-header text-center">
              <h1 className="page-title">Gallery Collections</h1>
              <div className="title-underline"></div>
              <p className="intro-text">
                Explore our image gallery by collections. Tap on a collection to
                view its images.
              </p>
            </div> */}
            <div className="d-flex flex-column text-center align-items-center">
              <h1
                className="text-center page-title"
                style={{ color: "#1a5276", fontWeight: "bold" }}
              >
                Gallery Collections
                <div
                  className="rounded-pill mt-2 bg-warning"
                  style={{
                    height: "5px",
                    width: "15%",
                  }}
                ></div>
              </h1>
            </div>
            <p className="intro-text">
              Explore our image gallery by collections. Tap on a collection to
              view its images.
            </p>

            {loading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Loading collections...</p>
              </div>
            ) : groups.length === 0 ? (
              <div className="no-content-message text-center">
                <p>No gallery collections found.</p>
              </div>
            ) : (
              <Row className="gallery-groups">
                {groups.map((group) => (
                  <Col
                    xl={3}
                    lg={4}
                    md={6}
                    key={group.groupName}
                    className="gallery-group-col"
                  >
                    <Card
                      className="gallery-group-card"
                      onClick={() => fetchGroupImages(group.groupName)}
                    >
                      <div className="card-image-container">
                        <Card.Img
                          variant="top"
                          src={`${group.previewImage}`}
                          alt={group.groupName}
                        />
                        <div className="card-overlay">
                          <Button className="view-gallery-btn">
                            View Gallery
                          </Button>
                        </div>
                      </div>
                      <Card.Body>
                        <Card.Title>{group.groupName}</Card.Title>
                        {group.imageCount && (
                          <Badge bg="secondary" className="image-count-badge">
                            {group.imageCount} images
                          </Badge>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </>
        )}

        {selectedGroup && (
          <>
            <div className="gallery-header with-back-btn">
              <Button
                className="back-button"
                onClick={() => setSelectedGroup(null)}
              >
                <AiOutlineArrowLeft /> Back to Collections
              </Button>
              <h2 className="gallery-title">{selectedGroup}</h2>
            </div>

            {loading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Loading images...</p>
              </div>
            ) : (
              <Row className="gallery-images">
                {images.map((img, idx) => (
                  <Col
                    xl={2}
                    lg={3}
                    md={4}
                    sm={6}
                    xs={6}
                    key={img.id}
                    className="gallery-image-col"
                  >
                    <div
                      className="gallery-image-container"
                      onClick={() => openLightbox(idx)}
                    >
                      <img src={`${img.imageUrl}`} alt="" loading="lazy" />
                      <div className="image-hover-effect"></div>
                    </div>
                  </Col>
                ))}
              </Row>
            )}
          </>
        )}

        {/* Lightbox Modal */}
        <Modal
          show={lightbox.show}
          onHide={closeLightbox}
          centered
          size="xl"
          className="lightbox-modal"
          fullscreen="md-down"
        >
          <Modal.Header className="lightbox-header" closeButton>
            <Modal.Title>{selectedGroup}</Modal.Title>
            {/* <Button
              variant="light"
              className="close-button"
              onClick={closeLightbox}
              aria-label="Close"
            >
              <AiOutlineClose />
            </Button> */}
          </Modal.Header>
          <Modal.Body
            className="lightbox-body"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {images[lightbox.index] && (
              <div className="image-container">
                <img
                  src={`${images[lightbox.index].imageUrl}`}
                  alt=""
                  style={{
                    transform: `scale(${lightbox.zoom})`,
                    transition: "transform 0.2s ease",
                  }}
                  className="lightbox-image"
                />
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="lightbox-footer">
            <div className="lightbox-controls">
              <Button
                variant="light"
                onClick={prevImage}
                className="control-btn"
                aria-label="Previous image"
              >
                <AiOutlineLeft />
              </Button>

              <span className="image-counter">
                {lightbox.index + 1} / {images.length}
              </span>

              <Button
                variant="light"
                onClick={nextImage}
                className="control-btn"
                aria-label="Next image"
              >
                <AiOutlineRight />
              </Button>
            </div>

            <div className="zoom-controls">
              <Button
                variant="light"
                onClick={zoomOut}
                disabled={lightbox.zoom <= 1}
                className="control-btn"
                aria-label="Zoom out"
              >
                <AiOutlineZoomOut />
              </Button>
              <Button
                variant="light"
                onClick={zoomIn}
                disabled={lightbox.zoom >= 3}
                className="control-btn"
                aria-label="Zoom in"
              >
                <AiOutlineZoomIn />
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
};

export default PublicGallery;
